import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Star, ShoppingBag, Store, Megaphone, Shield, Package, MessageCircle, Settings, ChevronRight } from 'lucide-react';

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    navigate('/login');
    return null;
  }

  const quickLinks = [
    { icon: ShoppingBag, label: 'Mis compras', desc: 'Ver historial de pedidos', path: '/my-purchases', color: 'text-zl-orange', bg: 'bg-zl-orange-100' },
    { icon: Package, label: 'Mis publicaciones', desc: 'Gestionar productos', path: '/seller/products', color: 'text-green-600', bg: 'bg-green-100' },
    { icon: Store, label: 'Mis ventas', desc: 'Ver órdenes recibidas', path: '/seller/orders', color: 'text-blue-600', bg: 'bg-blue-100' },
    { icon: MessageCircle, label: 'Preguntas', desc: 'Preguntas de compradores', path: '/seller/questions', color: 'text-purple-600', bg: 'bg-purple-100' },
    { icon: Megaphone, label: 'Mis campañas', desc: 'Gestionar anuncios', path: '/advertiser/campaigns', color: 'text-amber-600', bg: 'bg-amber-100' },
    { icon: Settings, label: 'Configurar tienda', desc: 'Logo, dirección, ubicación', path: '/store-config', color: 'text-gray-600', bg: 'bg-gray-100' },
  ];

  return (
    <div className="space-y-6">
        {/* Header Card */}
        <Card className="p-6" data-testid="profile-header">
          <div className="flex items-start gap-6">
            <Avatar className="w-24 h-24">
              <AvatarImage src={user.avatar} />
              <AvatarFallback className="text-2xl">{user.name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-1">{user.name}</h1>
              <p className="text-gray-600 mb-3">{user.email}</p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 mr-1" />
                  <span className="font-semibold">{user.reputation || 5}</span>
                  <span className="text-gray-500 ml-1">Reputación</span>
                </div>
                <div>
                  <span className="font-semibold">{user.purchases || 0}</span>
                  <span className="text-gray-500 ml-1">Compras</span>
                </div>
                <div>
                  <span className="font-semibold">{user.sales || 0}</span>
                  <span className="text-gray-500 ml-1">Ventas</span>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/account/personal')}
              className="shrink-0"
            >
              Editar perfil
            </Button>
          </div>
        </Card>

        {/* Quick Actions Grid */}
        <h2 className="text-lg font-bold mb-4">Accesos rápidos</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {quickLinks.map(({ icon: Icon, label, desc, path, color, bg }) => (
            <Card
              key={path}
              className="p-5 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all"
              onClick={() => navigate(path)}
              data-testid={`profile-link-${path.replace(/\//g, '-')}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-11 h-11 ${bg} rounded-full flex items-center justify-center shrink-0`}>
                  <Icon className={`w-5 h-5 ${color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">{label}</p>
                  <p className="text-sm text-gray-500 truncate">{desc}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
              </div>
            </Card>
          ))}
        </div>

        {/* Security Card */}
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 bg-red-100 rounded-full flex items-center justify-center shrink-0">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900">Seguridad y privacidad</p>
              <p className="text-sm text-gray-500">Contraseña, datos personales, direcciones</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/account/security')}>
              Configurar
            </Button>
          </div>
        </Card>
    </div>
  );
};

export default Profile;
